import type Alpine from 'alpinejs';
import {registerRequestRuntime, requestWithAlpine, type RequestOptions} from '@client/runtime/engine/request';
import {
    navigateWithAlpine,
    registerNavigationRuntime,
    type NavigationOptions,
} from '@client/runtime/engine/navigation';
import {isRecord, warn} from '../utils';

type RequestDirectiveContext = Pick<Alpine.DirectiveData, 'expression'>;
type RequestDirectiveOptions = NavigationOptions & Record<string, unknown>;

const REQUEST_ROOT_SELECTOR = '[x-ui-request], [x-ui-request\\.navigate]';
const OPTION_KEYS = new Set(['data', 'extra', 'headers', 'history', 'method']);
const CLICK_MODIFIER_KEYS: Array<keyof MouseEvent> = ['altKey', 'ctrlKey', 'metaKey', 'shiftKey'];
const nativeFormSubmissions = new WeakSet<HTMLFormElement>();

const readOptions = (
    el: Element,
    {expression}: RequestDirectiveContext,
    utilities: Alpine.DirectiveUtilities,
): RequestDirectiveOptions | null => {
    if (!expression?.trim()) return {};

    const evaluated = utilities.Alpine.evaluate(el, expression);
    if (!isRecord(evaluated)) {
        warn('x-ui-request: expression must evaluate to an object');
        return null;
    }

    Object.keys(evaluated).forEach((key) => {
        if (!OPTION_KEYS.has(key)) warn('x-ui-request: unknown option key', key);
    });

    return evaluated as RequestDirectiveOptions;
};

const submitterFrom = (event: SubmitEvent): HTMLElement | null => (
    event.submitter instanceof HTMLElement ? event.submitter : null
);

const shouldEnhanceFormSubmit = (form: HTMLFormElement, event: SubmitEvent, submitter: HTMLElement | null): boolean => {
    if (event.defaultPrevented) return false;
    if ((submitter?.getAttribute('formmethod') || form.getAttribute('method'))?.toLowerCase() === 'dialog') return false;
    if (form.getAttribute('target')) return false;
    if (submitter?.getAttribute('formtarget')) return false;

    if (!form.noValidate && !form.checkValidity()) {
        form.reportValidity();
        return false;
    }

    return true;
};

const samePageHashOnly = (url: URL): boolean => (
    url.hash !== ''
    && url.origin === window.location.origin
    && url.pathname === window.location.pathname
    && url.search === window.location.search
);

const shouldEnhanceAnchorClick = (anchor: HTMLAnchorElement, event: MouseEvent, navigate: boolean): boolean => {
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (navigate && CLICK_MODIFIER_KEYS.some((key) => event[key])) return false;
    if (anchor.hasAttribute('download')) return false;
    if (anchor.getAttribute('target')) return false;

    const url = new URL(anchor.href, document.baseURI);
    if (url.origin !== window.location.origin) return false;
    return !samePageHashOnly(url);
};

const registerFormDirective = (
    AlpineInstance: Alpine.Alpine,
    form: HTMLFormElement,
    directive: RequestDirectiveContext,
    utilities: Alpine.DirectiveUtilities,
    behavior: {navigate: boolean},
): void => {
    const onSubmit = (event: SubmitEvent): void => {
        if (nativeFormSubmissions.delete(form)) return;
        const submitter = submitterFrom(event);
        if (!shouldEnhanceFormSubmit(form, event, submitter)) return;

        const options = readOptions(form, directive, utilities);
        if (!options) return;

        event.preventDefault();
        if (behavior.navigate) {
            navigateWithAlpine(AlpineInstance, form.action, form, {
                ...options,
                source: form,
                submitter,
            });
            return;
        }
        void requestWithAlpine(AlpineInstance, undefined, {
            ...options,
            source: form,
            submitter,
        }).then((result) => {
            if (result.type !== 'unavailable') return;
            nativeFormSubmissions.add(form);
            form.requestSubmit(submitter ?? undefined);
        }).catch((error: unknown) => warn('x-ui-request: request failed', error));
    };

    form.addEventListener('submit', onSubmit);
    utilities.cleanup?.(() => form.removeEventListener('submit', onSubmit));
};

const registerAnchorDirective = (
    AlpineInstance: Alpine.Alpine,
    anchor: HTMLAnchorElement,
    directive: RequestDirectiveContext,
    utilities: Alpine.DirectiveUtilities,
    behavior: {navigate: boolean},
): void => {
    const onClick = (event: MouseEvent): void => {
        if (!shouldEnhanceAnchorClick(anchor, event, behavior.navigate)) return;

        const options = readOptions(anchor, directive, utilities);
        if (!options) return;

        event.preventDefault();
        if (behavior.navigate) {
            navigateWithAlpine(AlpineInstance, anchor.href, anchor, options);
            return;
        }
        void requestWithAlpine(AlpineInstance, anchor.href, {
            ...options,
            method: options.method ?? 'GET',
            source: anchor,
        }).then((result) => {
            if (result.type === 'unavailable') window.location.assign(anchor.href);
        }).catch((error: unknown) => warn('x-ui-request: request failed', error));
    };

    anchor.addEventListener('click', onClick);
    utilities.cleanup?.(() => anchor.removeEventListener('click', onClick));
};

const registerRequestDirective = (AlpineInstance: Alpine.Alpine): void => {
    AlpineInstance.addRootSelector(() => REQUEST_ROOT_SELECTOR);
    registerRequestRuntime(AlpineInstance);
    registerNavigationRuntime(AlpineInstance);

    AlpineInstance.directive('ui-request', (el, {expression, modifiers}, utilities) => {
        const directive = {expression};

        if (el instanceof HTMLFormElement) {
            registerFormDirective(AlpineInstance, el, directive, utilities, {
                navigate: modifiers.includes('navigate'),
            });
            return;
        }

        if (el instanceof HTMLAnchorElement) {
            registerAnchorDirective(AlpineInstance, el, directive, utilities, {
                navigate: modifiers.includes('navigate'),
            });
            return;
        }

        warn('x-ui-request: directive only supports forms and anchors', el);
    });
};

export {registerRequestDirective};
